<template>
    <div class="datafield-validation-rule">
        <div class="datafield-validation-rule__actions">
            <div class="datafield-validation-rule__order-actions">
                <button
                    class="info-button"
                    ref="orderInfo"
                    @mouseout="$refs.tooltipOrderInfo.close()"
                    @mouseover="$refs.tooltipOrderInfo.showAt($refs.orderInfo)"
                >
                    <i class="fa fa-info-circle"></i>
                </button>
                <ui5-popover ref="tooltipOrderInfo">
                    <div class="validation-rule-info">
                        <strong>{{ trans('pages.product_type_details.datafield_validation.execution_order') }}:</strong> {{ rule.order !== undefined ? rule.order : index }}<br>
                        <strong>{{ trans('pages.product_type_details.datafield_validation.rule_id') }}:</strong> {{ rule.id || trans('pages.product_type_details.datafield_validation.new') }}<br>
                        <strong>{{ trans('pages.product_type_details.datafield_validation.description') }}:</strong> {{ rule.description || '-' }}<br>
                        <hr class="validation-rule-info__divider">
                        <strong>{{ trans('pages.product_type_details.datafield_validation.conditions') }} ({{ rule.datafieldValidationConditions ? rule.datafieldValidationConditions.length : 0 }}):</strong><br>
                        <template v-if="rule.datafieldValidationConditions && rule.datafieldValidationConditions.length > 0">
                            <div v-for="(cond, idx) in rule.datafieldValidationConditions" :key="'cond-' + idx" class="validation-rule-info__item">
                                {{ idx + 1 }}. ID: {{ cond.id || '-' }}, {{ trans('pages.product_type_details.datafield_validation.datafield') }}: {{ cond.datafieldId || '-' }}, {{ trans('pages.product_type_details.datafield_validation.sort') }}: {{ cond.sort }}
                            </div>
                        </template>
                        <template v-else>
                            <span class="validation-rule-info__empty">{{ trans('pages.product_type_details.datafield_validation.no_conditions') }}</span><br>
                        </template>
                        <hr class="validation-rule-info__divider">
                        <strong>{{ trans('pages.product_type_details.datafield_validation.actions') }} ({{ rule.datafieldValidationActions ? rule.datafieldValidationActions.length : 0 }}):</strong><br>
                        <template v-if="rule.datafieldValidationActions && rule.datafieldValidationActions.length > 0">
                            <div v-for="(action, idx) in rule.datafieldValidationActions" :key="'action-' + idx" class="validation-rule-info__item">
                                {{ idx + 1 }}. ID: {{ action.id || '-' }}, {{ trans('pages.product_type_details.datafield_validation.type') }}: {{ action.action || '-' }}, {{ trans('pages.product_type_details.datafield_validation.datafield') }}: {{ action.datafieldId || '-' }}, {{ trans('pages.product_type_details.datafield_validation.sort') }}: {{ action.sort }}
                            </div>
                        </template>
                        <template v-else>
                            <span class="validation-rule-info__empty">{{ trans('pages.product_type_details.datafield_validation.no_actions') }}</span>
                        </template>
                    </div>
                </ui5-popover>
                <button
                    ref="ruleGrab"
                    class="handle"
                    @mouseout="$refs.tooltipDrag.close()"
                    @mouseover="$refs.tooltipDrag.showAt($refs.ruleGrab)"
                >
                    <uil-draggabledots />
                </button>
                <ui5-popover ref="tooltipDrag">
                    {{ trans('pages.product_type_details.datafield_validation.drag_tooltip') }}
                </ui5-popover>
                <button
                    v-show="index > 0"
                    ref="ruleUp"
                    @click="$emit('onMoveUp', index)"
                    @mouseout="$refs.tooltipMoveUp.close()"
                    @mouseover="$refs.tooltipMoveUp.showAt($refs.ruleUp)"
                >
                    <uil-angle-up />
                </button>
                <ui5-popover ref="tooltipMoveUp">
                    {{ trans('pages.product_type_details.datafield_validation.move_up_tooltip') }}
                </ui5-popover>
                <button
                    v-show="index < lastIndex"
                    ref="ruleDown"
                    @click="$emit('onMoveDown', index)"
                    @mouseout="$refs.tooltipMoveDown.close()"
                    @mouseover="$refs.tooltipMoveDown.showAt($refs.ruleDown)"
                >
                    <uil-angle-down />
                </button>
                <ui5-popover ref="tooltipMoveDown">
                    {{ trans('pages.product_type_details.datafield_validation.move_down_tooltip') }}
                </ui5-popover>
            </div>
        </div>
        <!-- CONDITIONS -->
        <h3 class="rules-title">
            {{ trans('pages.product_type_details.datafield_validation.conditions') }}
            <i
                @click="addCondition()"
                class="fa fa-plus"
            ></i>

            <div class="divider" />
            <i
                @click="deleteRule()"
                class="fa fa-trash"
            ></i>
        </h3>

        <draggable
            :list="rule.datafieldValidationConditions"
            @end="updateSort(rule.datafieldValidationConditions)"
        >
            <datafield-validation-condition
                v-for="(condition, index) of rule.datafieldValidationConditions"
                :key="index"
                :condition="condition"
                :datafields="datafields"
                :validation-errors="
                    validationErrors.datafieldValidationConditions.$each[index]
                "
                @delete="deleteCondition(index)"
            />
        </draggable>

        <!-- ACTIONS -->
        <h3 class="rules-title">
            {{ trans('pages.product_type_details.datafield_validation.actions') }}
            <i
                @click="addAction()"
                class="fa fa-plus"
            ></i>
        </h3>

        <draggable
            :list="rule.datafieldValidationActions"
            @end="updateSort(rule.datafieldValidationActions)"
        >
            <datafield-validation-action
                v-for="(action, index) of rule.datafieldValidationActions"
                :key="index"
                :action="action"
                :datafields="datafields"
                :validation-errors="
                    validationErrors.datafieldValidationActions.$each[index]
                "
                @delete="deleteAction(index)"
            />
        </draggable>
    </div>
</template>
<script>
import DatafieldValidationAction from './DatafieldValidationAction.vue';
import DatafieldValidationStatement from './DatafieldValidationStatement.vue';
import DatafieldValidationCondition from './DatafieldValidationCondition.vue';
import draggable from 'vuedraggable';
import localizationMixin from '../../mixins/localization';
import {
    UilDraggabledots,
    UilAngleUp,
    UilAngleDown,
} from '@iconscout/vue-unicons';

export default {
    mixins: [localizationMixin],
    props: {
        rule: {
            type: Object,
            required: true,
        },
        datafields: {
            type: Array,
            required: true,
        },
        validationErrors: {
            type: Object,
        },
        index: {
            type: Number,
            required: true,
        },
        lastIndex: {
            type: Number,
            required: true,
        },
    },
    methods: {
        deleteRule() {
            this.$emit('delete');
        },
        addCondition() {
            this.rule.datafieldValidationConditions.push({
                datafieldId: null,
                sort: this.rule.datafieldValidationConditions.length,
                cmpOperator: '=',
                cmpValue: null,
                cmpDatafieldId: null,
                cmpDatafieldOptionId: null,
                actionOperator: null,
                actionValue: null,
                actionDatafieldId: null,
                actionDatafieldOptionId: null,
            });
        },
        addAction() {
            this.rule.datafieldValidationActions.push({
                datafieldId: null,
                sort: this.rule.datafieldValidationActions.length,
                action: null,
                cmpOperator: '=',
                cmpValue: null,
                cmpDatafieldId: null,
                cmpDatafieldOptionId: null,
                actionOperator: null,
                actionValue: null,
                actionDatafieldId: null,
                actionDatafieldOptionId: null,
            });
        },
        deleteCondition(index) {
            this.rule.datafieldValidationConditions.splice(index, 1);

            // Update sort
            this.updateSort(this.rule.datafieldValidationConditions);
        },
        deleteAction(index) {
            this.rule.datafieldValidationActions.splice(index, 1);

            // Update sort
            this.updateSort(this.rule.datafieldValidationConditions);
        },
        updateSort(items) {
            for (const [index, item] of items.entries()) {
                item.sort = index;
            }
        },
    },
    mounted() {
        if (this.rule) {
            this.rule.datafieldValidationConditions.sort(
                (a, b) => Number(a.sort) - Number(b.sort)
            );
            this.rule.datafieldValidationActions.sort(
                (a, b) => Number(a.sort) - Number(b.sort)
            );
        }
    },
    components: {
        DatafieldValidationCondition,
        DatafieldValidationStatement,
        DatafieldValidationAction,
        draggable,
        UilDraggabledots,
        UilAngleUp,
        UilAngleDown,
    },
};
</script>
